import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, ArrowUpRight } from "lucide-react";
import { get } from "@/lib/api";
import Reveal from "@/components/site/Reveal";

const fmtMoney = (v) =>
    new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
    }).format(v || 0);

export default function PaySuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({
        phase: "polling", // polling | paid | failed | unknown
        attempts: 0,
        data: null,
        error: "",
    });
    const cancelled = useRef(false);

    useEffect(() => {
        document.title = "Payment Success | 90 Day Alpha";
    }, []);

    useEffect(() => {
        if (!sessionId) {
            setState({
                phase: "failed",
                attempts: 0,
                data: null,
                error: "Missing session_id",
            });
            return;
        }
        cancelled.current = false;
        const maxAttempts = 10;
        const intervalMs = 2000;

        const poll = async (attempt) => {
            if (cancelled.current) return;
            try {
                const data = await get(
                    `/payments/v1/checkout/status/${sessionId}`
                );
                if (data.payment_status === "paid") {
                    setState({
                        phase: "paid",
                        attempts: attempt,
                        data,
                        error: "",
                    });
                    return;
                }
                if (data.status === "expired") {
                    setState({
                        phase: "failed",
                        attempts: attempt,
                        data,
                        error: "Checkout session expired",
                    });
                    return;
                }
                if (attempt >= maxAttempts) {
                    setState({
                        phase: "unknown",
                        attempts: attempt,
                        data,
                        error: "Status check timed out",
                    });
                    return;
                }
                setState((s) => ({ ...s, attempts: attempt, data }));
                setTimeout(() => poll(attempt + 1), intervalMs);
            } catch (e) {
                setState({
                    phase: "failed",
                    attempts: attempt,
                    data: null,
                    error: "Could not verify payment.",
                });
            }
        };

        poll(1);
        return () => {
            cancelled.current = true;
        };
    }, [sessionId]);

    return (
        <div
            data-testid="pay-success-page"
            className="min-h-[100vh] electric-bg flex items-center justify-center px-6 py-32 relative overflow-hidden"
        >
            <div
                aria-hidden
                className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-[#1E90FF]/15 blur-[160px] animate-pulse-glow pointer-events-none"
            />
            <div className="relative max-w-2xl text-center">
                {state.phase === "polling" && (
                    <Reveal>
                        <Loader2
                            size={48}
                            className="text-[#00B7FF] animate-spin mx-auto"
                        />
                        <p className="mt-8 text-xs uppercase tracking-[0.3em] font-semibold text-[#00B7FF]">
                            Verifying Payment
                        </p>
                        <h1 className="mt-6 font-display font-black text-white text-3xl md:text-5xl tracking-tighter leading-[1.02]">
                            Hang tight…
                        </h1>
                        <p className="mt-4 text-white/65 text-sm">
                            Confirming your payment with Stripe. This usually
                            takes a few seconds.
                        </p>
                    </Reveal>
                )}

                {state.phase === "paid" && (
                    <Reveal>
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#1E90FF]/10 border border-[#1E90FF]/40 mb-2 glow-blue">
                            <CheckCircle2
                                size={40}
                                className="text-[#00B7FF]"
                                strokeWidth={1.5}
                            />
                        </div>
                        <p className="mt-8 text-xs uppercase tracking-[0.3em] font-semibold text-[#00B7FF]">
                            Welcome to 90 Day Alpha
                        </p>
                        <h1 className="mt-6 font-display font-black text-white text-4xl md:text-6xl tracking-tighter leading-[1.02]">
                            You're{" "}
                            <span className="text-[#D4AF37]">in</span>.
                        </h1>
                        <div
                            data-testid="pay-success-amount"
                            className="mt-8 inline-flex flex-col items-center gap-1 px-5 py-3 border border-[#1a2a4a] bg-[#060c1e]"
                        >
                            <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#00B7FF]">
                                Paid Today
                            </p>
                            <p className="text-white font-display font-black text-2xl tracking-tight">
                                {fmtMoney(state.data?.amount_usd)}
                            </p>
                            {state.data?.installments > 1 && (
                                <p className="text-white/55 text-[11px]">
                                    Plan total {fmtMoney(state.data?.total_program_usd)} · 2 more installments will be billed
                                </p>
                            )}
                        </div>
                        <p className="mt-8 text-white/75 leading-relaxed text-base">
                            Welcome aboard. Our team will reach out within 24
                            hours with your onboarding plan, week-one schedule,
                            and access details.
                        </p>
                        <div className="mt-10 flex flex-col sm:flex-row gap-4 items-center justify-center">
                            <Link
                                to="/"
                                data-testid="pay-success-home"
                                className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-[0.18em] text-xs hover:bg-[#C5A059] transition-all"
                            >
                                Back To Home <ArrowUpRight size={16} />
                            </Link>
                            <Link
                                to="/program"
                                data-testid="pay-success-program"
                                className="text-white/65 hover:text-[#00B7FF] uppercase tracking-[0.2em] text-xs font-semibold"
                            >
                                Read about the program →
                            </Link>
                        </div>
                    </Reveal>
                )}

                {(state.phase === "failed" || state.phase === "unknown") && (
                    <Reveal>
                        <AlertTriangle
                            size={48}
                            className="text-[#fca5a5] mx-auto"
                        />
                        <p className="mt-8 text-xs uppercase tracking-[0.3em] font-semibold text-[#fca5a5]">
                            {state.phase === "unknown"
                                ? "Status Pending"
                                : "Payment Issue"}
                        </p>
                        <h1 className="mt-6 font-display font-black text-white text-3xl md:text-4xl tracking-tighter">
                            {state.phase === "unknown"
                                ? "Still confirming with Stripe…"
                                : "We couldn't verify your payment."}
                        </h1>
                        <p className="mt-4 text-white/65 text-sm">
                            {state.error ||
                                "If you were charged, refresh this page in a minute or contact support."}
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-4 items-center justify-center">
                            <Link
                                to="/pricing"
                                data-testid="pay-fail-back"
                                className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-[0.18em] text-xs hover:bg-[#C5A059] transition-all"
                            >
                                Back To Pricing <ArrowUpRight size={16} />
                            </Link>
                            <Link
                                to="/book"
                                className="text-white/65 hover:text-[#00B7FF] uppercase tracking-[0.2em] text-xs font-semibold"
                            >
                                Or book a free consultation →
                            </Link>
                        </div>
                    </Reveal>
                )}
            </div>
        </div>
    );
}
